import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import authApiClient from '../authApiClient';

const NotificationContext = createContext();
const NOTIFICATION_DEBUG = String(process.env.REACT_APP_NOTIFICATION_DEBUG || '').trim() === '1';
const notificationDebug = (...args) => {
  if (NOTIFICATION_DEBUG) window.console.log(...args);
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [friendRequests, setFriendRequests] = useState([]);
  const [pendingQuizShares, setPendingQuizShares] = useState([]);
  const [pendingChallengeShares, setPendingChallengeShares] = useState([]);
  const [pendingRecommendationShares, setPendingRecommendationShares] = useState([]);
  const [practitionerNotifications, setPractitionerNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);
  const [isFetching, setIsFetching] = useState(false);

  const inFlightRef = useRef(false);
  const lastFetchRef = useRef(0);
  const pollIntervalRef = useRef(null);

  const fetchNotifications = useCallback(async (options = {}) => {
    const force = Boolean(options?.force);
    const { waitForSessionReady } = await import('../utils/authManager');
    try {
      await waitForSessionReady();
    } catch (error) {
      notificationDebug('Skipping notification fetch - session not ready');
      return;
    }

    if (typeof window !== 'undefined' && window.__RATE_LIMITED) {
      const rateLimitUntil = window.__RATE_LIMIT_UNTIL || 0;
      if (Date.now() < rateLimitUntil) {
        notificationDebug('Skipping notification fetch - globally rate limited');
        return;
      }
    }

    if (typeof window !== 'undefined') {
      const pathname = String(window.location?.pathname || '').toLowerCase();
      const guideFocused = pathname.includes('/journey-guide');
      if (guideFocused) {
        notificationDebug('Skipping notification fetch - journey guide focused');
        return;
      }
    }

    if (inFlightRef.current) {
      notificationDebug('Skipping notification fetch - already in progress');
      return;
    }

    const now = Date.now();
    if (!force && lastFetchRef.current && (now - lastFetchRef.current) < 5000) {
      notificationDebug('Skipping notification fetch - rate limited');
      return;
    }

    try {
      inFlightRef.current = true;
      setIsFetching(true);
      setLoading(true);
      notificationDebug('Fetching notifications...');

      const [requestsRes, notificationsRes] = await Promise.all([
        authApiClient.get('/friends/requests'),
        authApiClient.get('/notifications?limit=50')
      ]);

      setFriendRequests(requestsRes.data.requests || []);

      const notifications = notificationsRes.data.notifications || [];
      const unreadNotifications = notifications.filter((notif) => !notif.isRead);
      notificationDebug('Received notifications:', notifications.length);

      const quizShares = unreadNotifications.filter(notif =>
        notif.type === 'quiz_share' || notif.type === 'quiz_share_topic'
      );
      notificationDebug('Quiz share notifications:', quizShares.length);
      setPendingQuizShares(quizShares);

      const challengeShares = unreadNotifications.filter(notif =>
        notif.type === 'challenge_share'
      );
      notificationDebug('Challenge share notifications:', challengeShares.length);
      setPendingChallengeShares(challengeShares);

      const recommendationShares = unreadNotifications.filter(notif =>
        notif.type === 'recommendation_share'
      );
      notificationDebug('Recommendation share notifications:', recommendationShares.length);
      setPendingRecommendationShares(recommendationShares);

      const practitionerUpdates = unreadNotifications.filter(notif =>
        notif.type === 'practitioner_connection_request'
        || notif.type === 'practitioner_sharing_preferences_updated'
        || notif.type === 'practitioner_action_sent'
        || notif.type === 'practitioner_action_response'
        || notif.type === 'practitioner_goal_shared'
        || notif.type === 'practitioner_challenge_shared'
        || notif.type === 'practitioner_evaluation_shared'
        || notif.type === 'practitioner_progress_shared'
      );
      notificationDebug('Practitioner notifications:', practitionerUpdates.length);
      setPractitionerNotifications(practitionerUpdates);

      const fetchedAt = Date.now();
      lastFetchRef.current = fetchedAt;
      setLastFetch(fetchedAt);
    } catch (error) {
      console.error('Error fetching notifications:', error);

      if (error.response?.status === 429 || error.response?.status === 401) {
        console.warn('Notifications fetch stopped due to rate limit or auth failure');
        return;
      }

      setFriendRequests([]);
      setPendingQuizShares([]);
      setPendingChallengeShares([]);
      setPendingRecommendationShares([]);
      setPractitionerNotifications([]);
    } finally {
      setLoading(false);
      setIsFetching(false);
      inFlightRef.current = false;
    }
  }, []);

  const getCommunityNotificationCount = () => {
    return (
      (friendRequests?.length || 0)
      + (pendingQuizShares?.length || 0)
      + (pendingChallengeShares?.length || 0)
      + (pendingRecommendationShares?.length || 0)
    );
  };

  const getPractitionerNotificationCount = () => practitionerNotifications?.length || 0;

  const markAsRead = useCallback(() => {
    fetchNotifications({ force: true });
  }, [fetchNotifications]);

  useEffect(() => {
    let mounted = true;

    const initNotifications = async () => {
      const { waitForSessionReady } = await import('../utils/authManager');
      try {
        await waitForSessionReady();
        notificationDebug('[NotificationContext] Session ready, starting notification polling');

        if (mounted) {
          fetchNotifications();
        }

        if (mounted) {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          pollIntervalRef.current = setInterval(() => {
            if (mounted && document.visibilityState === 'visible') {
              fetchNotifications();
            }
          }, 60000);
        }
      } catch (error) {
        notificationDebug('[NotificationContext] Session not ready, skipping notification setup');
      }
    };

    initNotifications();

    return () => {
      mounted = false;
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [fetchNotifications]);

  const value = {
    friendRequests,
    setFriendRequests,
    pendingQuizShares,
    pendingChallengeShares,
    pendingRecommendationShares,
    practitionerNotifications,
    loading,
    isFetching,
    communityNotificationCount: getCommunityNotificationCount(),
    practitionerNotificationCount: getPractitionerNotificationCount(),
    notificationCount: getCommunityNotificationCount() + getPractitionerNotificationCount(),
    fetchNotifications,
    markAsRead,
    lastFetch,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
